// Decrypted chat transcript for a visit — messages, images, intake forms and
// replies. Gated behind a password re-entry (same modal as the Patients nav).
// Messages come from the SendBird archive (api/v2/chatHistory) encrypted with
// the visit's sharedKey; decryption happens client-side, the key never leaves
// this view.
import { useEffect, useMemo, useRef, useState } from "react";
import { Header } from "./Header";
import { VISIT_TABLE_VIEW } from "./ConstantsViews";
import { GetVisitChatTranscript } from "Services/API/Patients.service";
import { decodeBase64, decrypt } from "Helpers/ChatEncryption";
import { errorFormatter } from "Helpers/ErrorMsgHandler";
import { formatDate2 } from "Helpers/DateFormat";
import { Spinner } from "Components/Common/LoadingSpinner/Spinner";
import { ErrorPopUp } from "Components/Common/PopUp/PopUp";
import { UserAuthenticationModal } from "Pages/Patients/MainPage/UserModal";
import { isPatientGateUnlocked, markPatientGateUnlocked } from "Helpers/PatientGate";
import { ReactComponent as PasswordIcon } from "Assets/PatientPassword.svg";
import "Colors.css";

// Verbatim from the mobile apps' SendBird customType values
const MESSAGE_TYPES = {
  SendBirdAutoMessage: "SENDBIRD:AUTO_EVENT_MESSAGE",
  Image: "IMAGE",
  IntakeForm: "INTAKE FORM",
  IntakeFormReply: "INTAKE FORM REPLY",
};

const asText = (v) => (typeof v === "string" ? v : v == null ? "" : JSON.stringify(v));

/** api/v2/chatHistory items: { id, createdAt, doctorId, message: { message,
 * type, data, user: { user_id } } } — mapping mirrors the provider app's
 * archived-transcript logic (minus Matrix, which admin does not surface). */
const mapArchive = (items, sharedKey) => {
  const out = [];
  for (const item of items ?? []) {
    const inner = item?.message;
    if (!inner) continue;
    const type = inner.type;
    const decrypted = decrypt(sharedKey, inner.message);
    const base = {
      id: String(item.id ?? `${out.length}`),
      isDoctor: !!item.doctorId && inner.user?.user_id === String(item.doctorId),
      createdAt: item.createdAt,
    };
    if (type === MESSAGE_TYPES.Image) {
      // URL lives encrypted in message.data; older messages carry it as the
      // encrypted body itself
      let url = "";
      if (inner.data) {
        const d = decrypt(sharedKey, inner.data);
        if (typeof d === "string" && /^https?:\/\//.test(d)) url = d;
      }
      if (!url && typeof decrypted === "string" && decrypted.startsWith("http")) {
        url = decrypted;
      }
      out.push({ ...base, kind: "image", imageUrl: url });
    } else if (type === MESSAGE_TYPES.IntakeForm || type === MESSAGE_TYPES.IntakeFormReply) {
      out.push({
        ...base,
        kind: type === MESSAGE_TYPES.IntakeForm ? "form" : "formReply",
        text: asText(decrypted),
        formData: inner.data ? decrypt(sharedKey, inner.data) : null,
      });
    } else if (type === MESSAGE_TYPES.SendBirdAutoMessage) {
      const msg = asText(decrypted);
      if (msg && !msg.includes("The channel is created")) {
        out.push({ ...base, kind: "info", text: msg });
      }
    } else {
      out.push({ ...base, kind: "text", text: asText(decrypted) });
    }
  }
  return out.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
};

const FormCard = ({ message }) => {
  const items = Array.isArray(message.formData)
    ? message.formData.filter((item) => item && item.disabled !== true && item.deleted !== true)
    : [];
  return (
    <div>
      <div className="font-medium">{message.text}</div>
      {items.length > 0 && (
        <ul className="list-disc ml-5 mt-1">
          {items.map((item, index) => (
            <li key={index} className="text-sm">
              {item.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const MessageBubble = ({ message }) => {
  if (message.kind === "info") {
    return (
      <div className="text-center text-xs light_grayish_navy py-1">{message.text}</div>
    );
  }
  const bubbleColors = message.isDoctor
    ? "text-white"
    : "bg-gray-100 sapphire";
  return (
    <div className={`flex flex-col ${message.isDoctor ? "items-end" : "items-start"}`}>
      <div
        className={`max-w-lg rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${bubbleColors}`}
        style={message.isDoctor ? { backgroundColor: "#37828B" } : {}}
      >
        {message.kind === "image" &&
          (message.imageUrl ? (
            <img
              src={message.imageUrl}
              alt="Chat attachment"
              className="max-w-xs rounded cursor-pointer"
              onClick={() => window.open(message.imageUrl, "_blank", "noopener")}
            />
          ) : (
            <span className="italic">Image unavailable</span>
          ))}
        {(message.kind === "form" || message.kind === "formReply") && (
          <FormCard message={message} />
        )}
        {message.kind === "text" && message.text}
      </div>
      <span className="text-xs light_grayish_navy mt-0.5">
        {message.isDoctor ? "Provider" : "Patient"} · {formatDate2(message.createdAt)}
      </span>
    </div>
  );
};

export function VisitChat({ setActiveView, visitId, docName }) {
  // same 24h unlock window as the Patients tab gate (Helpers/PatientGate)
  const [authed, setAuthed] = useState(() => isPatientGateUnlocked());
  const authedRef = useRef(authed);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");
  const [transcript, setTranscript] = useState(null);

  useEffect(() => {
    if (!authed || !visitId) return;
    setLoading(true);
    GetVisitChatTranscript(visitId)
      .then((res) => setTranscript(res))
      .catch((err) => {
        console.error("[GetVisitChatTranscript][API_ERROR] -", err);
        setApiError(errorFormatter(err?.response?.data?.errors || err?.message));
      })
      .finally(() => setLoading(false));
  }, [authed, visitId]);

  const messages = useMemo(() => {
    if (!transcript?.sharedKey || !transcript?.messages?.data) return [];
    return mapArchive(transcript.messages.data, decodeBase64(transcript.sharedKey));
  }, [transcript]);

  if (!authed) {
    return (
      <UserAuthenticationModal
        icon={<PasswordIcon className="h-6 w-6" />}
        heading="Re-enter Password"
        content="To view the decrypted chat, please enter your password"
        btnText="Continue"
        modalOpen={true}
        setShowPatientsTable={() => {
          markPatientGateUnlocked();
          authedRef.current = true;
          setAuthed(true);
        }}
        setIsAuthModalOpen={() => {
          if (!authedRef.current) setActiveView(VISIT_TABLE_VIEW);
        }}
      />
    );
  }

  return (
    <div className="h-full">
      <Header onBack={setActiveView} title={`Chat — ${docName || "Visit"}`} />
      {apiError && <ErrorPopUp error={apiError} setApiError={setApiError} />}
      {loading && <Spinner />}
      {!loading && !apiError && transcript && !transcript.sharedKey && (
        <div className="p-4 text-sm light_grayish_navy">
          This visit has no encryption key — there is no chat to decrypt.
        </div>
      )}
      {!loading && transcript?.sharedKey && messages.length === 0 && (
        <div className="p-4 text-sm light_grayish_navy">No messages found for this visit.</div>
      )}
      <div className="flex flex-col gap-3 p-4">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} />
        ))}
      </div>
    </div>
  );
}
